import { useEffect, useRef, useState } from "react";
import {
  type LiveRange,
  type LiveSeries,
  fetchLiveSeries,
} from "../api";
import { LineChart } from "./LineChart";
import { RangeSelector } from "./RangeSelector";

/**
 * Single-metric power chart card (PV or Load), reused inline on the
 * Live tab and inside a Modal from the Home tiles. Polling cadence
 * matches the Live chart's (POLL_MS per range); worst case three
 * series components poll /api/live/series together, each a tiny JSON.
 * docs/customer-app.md > Front-end > Live tab polling
 */

const POLL_MS: Record<LiveRange, number> = {
  "15m": 10_000,
  "1h":  30_000,
  "6h":  5 * 60_000,
  "24h": 30 * 60_000,
};

export type DetailMetric =
  | "pv"
  | "load"
  | "battery_in"
  | "battery_out"
  | "grid_in"
  | "grid_out"
  | "ev";

interface Props {
  metric: DetailMetric;
  /** Initial range; user can change via the picker. */
  initialRange?: LiveRange;
  /** Override the inner chart height (compact in popup vs roomy on Live). */
  chartHeight?: number;
  /** Chart style. Default "line" - the inline Live-tab cards show the
   *  smooth-curve view that PV / Load look natural in. Modals opened
   *  from a tile pass "bar" so the drill-in feels like a different
   *  visualisation, not a clone of what's already on the tab. */
  chartMode?: "line" | "bar";
}

// Half-wave rectifiers for battery + grid: the underlying series is
// signed (battery_w > 0 = charging into batt, grid_w > 0 = importing).
// Splitting into in/out tiles means a "Opgeladen" view shows only the
// positive side, "Ontladen" only the negative side flipped to positive.
function clipPositive(xs: number[]): number[] {
  return xs.map((v) => Math.max(0, v));
}
function clipNegativeAbs(xs: number[]): number[] {
  return xs.map((v) => Math.max(0, -v));
}

const META: Record<
  DetailMetric,
  { label: string; color: string; pickValues: (s: LiveSeries) => number[] }
> = {
  pv: {
    label: "Opgewekt",
    color: "var(--color-sun)",
    pickValues: (s) => s.pv_w,
  },
  load: {
    label: "Verbruikt",
    color: "var(--color-home)",
    pickValues: (s) => s.load_w,
  },
  battery_in: {
    label: "Opgeladen",
    color: "var(--color-accent)",
    pickValues: (s) => clipPositive(s.battery_w),
  },
  battery_out: {
    label: "Ontladen",
    color: "var(--color-accent)",
    pickValues: (s) => clipNegativeAbs(s.battery_w),
  },
  grid_in: {
    label: "Geïmporteerd",
    color: "var(--color-grid)",
    pickValues: (s) => clipPositive(s.grid_w),
  },
  grid_out: {
    label: "Geëxporteerd",
    color: "var(--color-grid)",
    pickValues: (s) => clipNegativeAbs(s.grid_w),
  },
  ev: {
    label: "Auto geladen",
    color: "var(--color-ev)",
    // Sampled ~15-min by ems-engine - steppier than the Venus series,
    // still the honest record of the wallbox draw.
    pickValues: (s) => s.ev_w ?? s.t.map(() => 0),
  },
};

// De auto-SOC (0-100) over de laadbalken heen - alleen voor de
// ev-metric; gestreepte inktlijn zodat hij zich van de balken
// onderscheidt. Gaten = auto los / pass gemist -> lijn breekt.
const EV_SOC_COLOR = "var(--color-ink-mute)";

export function MetricSeriesCard({
  metric,
  initialRange = "6h",
  chartHeight = 220,
  chartMode = "line",
}: Props) {
  const [range, setRange] = useState<LiveRange>(initialRange);
  const [series, setSeries] = useState<LiveSeries | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      const my = ++seq.current;
      try {
        const v = await fetchLiveSeries(range);
        if (!cancelled && my === seq.current) {
          setSeries(v);
          setError(null);
        }
      } catch (e) {
        if (!cancelled && my === seq.current) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    }
    poll();
    const id = window.setInterval(poll, POLL_MS[range]);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [range]);

  const meta = META[metric];

  return (
    <section className="card p-4 flex flex-col gap-3">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="inline-block w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: meta.color }}
          />
          <h3 className="text-sm font-medium">{meta.label}</h3>
        </div>
        <RangeSelector value={range} onChange={setRange} />
      </header>

      {error && (
        <div className="text-xs text-danger">{error}</div>
      )}

      {series ? (
        <LineChart
          t={series.t}
          height={chartHeight}
          mode={chartMode}
          // Every detail metric is non-negative - PV / load are always
          // ≥0; battery_in / out + grid_in / out are half-wave-rectified
          // upstream. Floor at 0 so bars sit on the baseline (no
          // floating "-250 W" tick under positive-only data).
          floorAtZero
          series={[
            { name: meta.label, color: meta.color, values: meta.pickValues(series) },
          ]}
          overlayPct={
            metric === "ev" && series.ev_soc_pct
              ? { values: series.ev_soc_pct, color: EV_SOC_COLOR }
              : null
          }
        />
      ) : (
        <div
          className="grid place-items-center text-ink-mute text-sm"
          style={{ height: chartHeight }}
        >
          gegevens laden…
        </div>
      )}
      {metric === "ev" && series?.ev_soc_pct && (
        <div className="text-xs text-ink-mute flex items-center gap-1.5 px-1">
          <span className="inline-block w-4 border-t-2 border-dashed"
                style={{ borderColor: "var(--color-ink-mute)" }} />
          accu van de auto (%)
        </div>
      )}
    </section>
  );
}
